import { useNavigate } from "react-router-dom";
import MenuTop from "../../../../components/menu/top/menu-top";
import './style.css';
import { useEffect, useState } from "react";
import { fetchBeneficiariesService, fetchBeneficiaryTypesService } from "../../../../features/beneficiary/service/beneficiary-service";
import Loader from "../../../../components/loader/loader";
import MenuContext, { DISPLAY_TYPE } from "../../../../components/menu/context/menu-context";
import { Beneficiary } from "../../../../shared/interfaces/beneficiary";
import { BeneficiaryType } from "../../../../shared/interfaces/beneficiary-type";
import { Box, List, ListItem, ListItemText, Paper, Typography } from "@mui/material";

type SortField = 'name' | 'type';
type SortDirection = 'asc' | 'desc';

const sortableColumns: Array<{ key: SortField; label: string; flex: number }> = [
    { key: 'name', label: 'Nome', flex: 1.4 },
    { key: 'type', label: 'Tipo', flex: 1 },
];

export function ListBeneficiary() {
    const navigate = useNavigate();
    const [ isLoading, setIsLoading ] = useState(false);
    const [ beneficiaries, setBeneficiaries ] = useState<Beneficiary[]>([]);
    const [ beneficiaryTypes, setBeneficiaryTypes ] = useState<BeneficiaryType[]>([]);
    const [ sortField, setSortField ] = useState<SortField>('name');
    const [ sortDirection, setSortDirection ] = useState<SortDirection>('asc');

    const getTypeName = (rawType?: any) => {
        if (!rawType) return '';

        // type vindo como objeto { id, name } (caso algum endpoint hidrate)
        if (typeof rawType === 'object') {
            return rawType.name || rawType.id || '';
        }

        // type vindo como string (id ou nome direto)
        const byId = beneficiaryTypes.find((type) => type.id === rawType);
        if (byId) return byId.name;

        const byName = beneficiaryTypes.find(
            (type) => (type.name || '').toLowerCase() === String(rawType).toLowerCase()
        );
        if (byName) return byName.name;

        return String(rawType);
    };

    const getSortValue = (beneficiary: Beneficiary, field: SortField) => {
        switch (field) {
            case 'type':
                return getTypeName(beneficiary.type);
            case 'name':
            default:
                return beneficiary.name || '';
        }
    };

    const handleSort = (field: SortField) => {
        if (field === sortField) {
            setSortDirection((prevDirection) => prevDirection === 'asc' ? 'desc' : 'asc');
            return;
        }

        setSortField(field);
        setSortDirection('asc');
    };

    const sortedBeneficiaries = [...beneficiaries].sort((currentBeneficiary, nextBeneficiary) => {
        const currentValue = getSortValue(currentBeneficiary, sortField);
        const nextValue = getSortValue(nextBeneficiary, sortField);
        const comparison = currentValue.localeCompare(nextValue, 'pt-BR', {
            sensitivity: 'base',
            numeric: true,
        });

        return sortDirection === 'asc' ? comparison : comparison * -1;
    });

    useEffect(() => {
        const fetchBeneficiaries = async () => {
            try {
                setIsLoading(true);
                const [beneficiaries, types] = await Promise.all([
                    fetchBeneficiariesService(),
                    fetchBeneficiaryTypesService(),
                ]);

                setBeneficiaries(beneficiaries);
                setBeneficiaryTypes(types);
            } catch (error) {
                console.log(error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchBeneficiaries();
    }, []);

    return (
        <div className="container-client">
            <MenuTop />
            {isLoading && <Loader />}
            <div className='container-client__content'>
                <div className="container-client__content__title">
                    <h1 className="container-client__content__title-text">Listagem de beneficiários</h1>
                    <MenuContext
                        display={DISPLAY_TYPE.INLINE}
                        clickedAdd={() => navigate('/beneficiario')}
                    />
                </div>

                {beneficiaries.length > 0 ? (
                    <Paper
                        elevation={0}
                        sx={{
                            borderRadius: 2,
                            overflowX: 'auto',
                            border: '1px solid #f0f0f0',
                            width: '100%',
                            margin: '0 auto'
                        }}
                    >
                        <Box sx={{ minWidth: 840 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    padding: '12px 20px',
                                    backgroundColor: '#fafafa',
                                    borderBottom: '1px solid #f0f0f0'
                                }}
                            >
                                {sortableColumns.map((column) => {
                                    const isActiveColumn = sortField === column.key;
                                    const directionIndicator = !isActiveColumn
                                        ? '↕'
                                        : sortDirection === 'asc'
                                            ? '↑'
                                            : '↓';

                                    return (
                                        <Box
                                            component="button"
                                            key={column.key}
                                            onClick={() => handleSort(column.key)}
                                            type="button"
                                            sx={{
                                                flex: column.flex,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.75,
                                                border: 0,
                                                background: 'transparent',
                                                padding: 0,
                                                cursor: 'pointer',
                                                textAlign: 'left',
                                                color: isActiveColumn ? '#4db0b1' : '#4a4a4a',
                                                fontSize: '0.875rem',
                                                fontWeight: 600
                                            }}
                                        >
                                            <Typography
                                                component="span"
                                                variant="subtitle2"
                                                sx={{
                                                    fontWeight: 600,
                                                    color: 'inherit'
                                                }}
                                            >
                                                {column.label}
                                            </Typography>
                                            <Typography
                                                component="span"
                                                variant="caption"
                                                sx={{
                                                    color: isActiveColumn ? '#4db0b1' : '#9e9e9e',
                                                    fontWeight: 700
                                                }}
                                            >
                                                {directionIndicator}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Box>

                            <List disablePadding>
                                {sortedBeneficiaries.map((beneficiary, index) => (
                                    <ListItem
                                        key={beneficiary.id || index}
                                        onClick={() => navigate(`/beneficiario/${beneficiary.id}`)}
                                        sx={{
                                            display: 'flex',
                                            padding: '12px 20px',
                                            borderBottom: index < sortedBeneficiaries.length - 1 ? '1px solid #f0f0f0' : 'none',
                                            transition: 'background-color 0.2s',
                                            '&:hover': {
                                                backgroundColor: 'rgba(0, 0, 0, 0.03)',
                                                cursor: 'pointer',
                                            }
                                        }}
                                    >
                                        <ListItemText
                                            primary={<Typography variant="body2" noWrap>{beneficiary.name || '-'}</Typography>}
                                            sx={{ flex: sortableColumns[0].flex, margin: 0 }}
                                        />
                                        <ListItemText
                                            primary={<Typography variant="body2" noWrap>{getTypeName(beneficiary.type) || '-'}</Typography>}
                                            sx={{ flex: sortableColumns[1].flex, margin: 0 }}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    </Paper>
                ) : (
                    !isLoading && <p className="empty-message">Nenhum beneficiário encontrado.</p>
                )}
            </div>
        </div>
    )
}
